/**
 * `useImagePresignedUrl` — shape-identical companion to
 * `useFilePresignedUrl`, scoped to the wiki image NodeView surface.
 *
 * Image attachments on the wiki surface live in the private tenant
 * uploads bucket; the BE mints a signed GET URL with a 1h TTL for
 * wiki kinds (`PRESIGN_GET_TTL_SECONDS_WIKI` on the BE). This hook
 * mints once and holds the URL for the duration of the mount —
 * `<img onError>` triggers `refresh()` to mint a new URL if the rare
 * post-TTL eviction-and-reload race fires. Once the browser has
 * decoded the bitmap, the URL going stale doesn't matter: the bytes
 * stay in the image decode cache for the life of the page.
 *
 * No `refetchInterval` by design — see `use-file-presigned-url.ts` for
 * the full rationale (steady BE heartbeat would prevent Aurora SV2
 * staging auto-pause).
 *
 * Disabled when `attachmentId` is empty/undefined so the call shape is
 * ergonomic during the uploading branch of the NodeView (no fileId
 * yet — the `uploadingBlobUrl` attr drives the render).
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'

/** Stable cache key — one entry per attachment id. */
export const IMAGE_PRESIGNED_URL_QUERY_KEY = (attachmentId: string) =>
  ['wiki-image-presigned', attachmentId] as const

/**
 * BE response shape for `GET /api/uploads/:id/url?as=json`. Mirrors the
 * `SignedGetResponse` used by the uploads + file-presigned-url hooks.
 */
interface SignedGetResponse {
  data: {
    url: string
    contentType: string
    originalFilename: string
  }
}

export interface UseImagePresignedUrlResult {
  /** The presigned GET URL, ready for `<img src>`. */
  url: string | undefined
  isLoading: boolean
  isError: boolean
  /**
   * HTTP status from the underlying `ApiError` when the request failed.
   * 403 → restricted, 404 → deleted; the NodeView renders the canonical
   * "Image unavailable" fallback for both.
   */
  status: number | null
  /** Imperative refresh — call from `<img onError>` recovery paths. */
  refresh: () => Promise<void>
}

export function useImagePresignedUrl(
  attachmentId: string | null | undefined,
): UseImagePresignedUrlResult {
  const query = useQuery({
    queryKey: IMAGE_PRESIGNED_URL_QUERY_KEY(attachmentId ?? ''),
    queryFn: async () => {
      const res = await apiClient<SignedGetResponse>(`/api/uploads/${attachmentId}/url?as=json`)
      return res.data
    },
    enabled: Boolean(attachmentId),
    // Hold the URL for the lifetime of the mount — once `<img>` has
    // decoded the bitmap, the URL going stale doesn't matter. The
    // `<img onError>` path in `WikiImageNodeView` calls `refresh()` if
    // the browser ever needs to re-fetch (memory-pressure eviction or
    // open-doc-past-TTL reload). Avoids the steady BE heartbeat that
    // pinned Aurora SV2 staging awake.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const errorObj = query.error
  const status =
    errorObj && typeof errorObj === 'object' && 'status' in errorObj
      ? (((errorObj as { status: unknown }).status as number | null) ?? null)
      : null

  return {
    url: query.data?.url,
    isLoading: query.isLoading,
    isError: query.isError,
    status,
    refresh: async () => {
      await query.refetch()
    },
  }
}
